import { useCallback, useContext, useMemo } from 'react'
import clsx from 'clsx'
import styled, { css } from 'styled-components'
import { rem } from 'polished'
import { FormattedMessage, useIntl } from 'react-intl'
import ModalContext from 'contexts/ModalContext'
import Clickable from 'components/Common/Clickable'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import Image from 'components/Common/Image'
import FormatCurrency from 'components/Common/FormatCurrency'
import FlightDealModal from 'components/OfferPage/FlightDeal/FlightDealModal'
import { sortBy } from 'lib/array/arrayUtils'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { shouldShowFlightDealAvailabilityCta } from 'components/OfferPage/FlightDeal/flightDealDateUtils'

const BannerSurface = styled(Group)`
  padding: 0;
  background-color: transparent;
`

const bannerActionStyles = css`
  display: inline-flex;
  text-align: start;
`

const BannerButton = styled(Clickable)`
  ${bannerActionStyles}
`

const BannerContainer = styled.div`
  ${bannerActionStyles}
`

const LogoRing = styled.div`
  width: ${rem(26)};
  height: ${rem(26)};
  border-radius: 50%;
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  overflow: hidden;
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  & + & {
    margin-inline-start: ${rem(-7)};
  }

  &.overlap-index-0 {
    z-index: 5;
  }

  &.overlap-index-1 {
    z-index: 4;
  }

  &.overlap-index-2 {
    z-index: 3;
  }

  &.overlap-index-3 {
    z-index: 2;
  }
`

const CarrierLogoImage = styled(Image)`
  width: auto;
  height: ${rem(20)};
  max-width: none;

  img {
    width: auto;
    height: ${rem(20)};
    max-width: none;
  }
`

interface CarrierLogoEntry {
  code: string
  carrierName: string
  image: App.Image
}

interface Props {
  deals?: Array<App.FlightDeal>
  fallbackFlightPrice?: {
    fareType?: App.FlightDeal['fareType']
    perPersonPrice: number
  }
}

const LOGO_RING_OVERLAP_CLASS_NAMES = [
  'overlap-index-0',
  'overlap-index-1',
  'overlap-index-2',
  'overlap-index-3',
] as const
const MAX_VISIBLE_CARRIER_LOGOS = LOGO_RING_OVERLAP_CLASS_NAMES.length - 1
const EMPTY_FLIGHT_DEALS: Array<App.FlightDeal> = []

function getLogoRingClassName(index: number) {
  return clsx(LOGO_RING_OVERLAP_CLASS_NAMES[index])
}

function FlashHotelBundledFlightMerchandisingBanner(props: Props) {
  const { deals = EMPTY_FLIGHT_DEALS, fallbackFlightPrice } = props
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const offerPageState = useContext(OfferPageStateContext)
  const dispatch = useOfferPageDispatch()

  const sortedDeals = useMemo(
    () => sortBy(deals, deal => deal.perAdultPrice, 'asc'),
    [deals],
  )

  const cheapestDeal = sortedDeals[0]
  const displayDeal = cheapestDeal
  const displayPrice = cheapestDeal?.perAdultPrice ?? fallbackFlightPrice?.perPersonPrice
  const isFallbackPrice = !cheapestDeal && !!fallbackFlightPrice
  const isOfferPage = !!(offerPageState.offer || offerPageState.bedbankOffer || offerPageState.property || offerPageState.tourV2Offer)
  const showCheckAvailabilityCta = useMemo(() => shouldShowFlightDealAvailabilityCta({
    isOfferPage,
    checkInDate: offerPageState.checkInDate,
    checkOutDate: offerPageState.checkOutDate,
    duration: offerPageState.duration,
    travelPeriods: displayDeal?.travelPeriods,
  }), [displayDeal?.travelPeriods, isOfferPage, offerPageState.checkInDate, offerPageState.checkOutDate, offerPageState.duration])
  const adultPriceUnitLabel = intl.formatMessage({
    id: '-f206TRRuHyl-LIHwsRx',
    defaultMessage: 'adult',
    description: 'Price sale unit label for an adult in pricing rows and compact upsell tiles',
  })
  const personPriceUnitLabel = intl.formatMessage({
    id: 'a-c1CI8Lm4liycNIJk3a',
    defaultMessage: 'person',
    description: 'Price sale unit label for a person in pricing rows and compact upsell tiles',
  })
  const priceUnitLabel = isFallbackPrice ? personPriceUnitLabel : adultPriceUnitLabel

  const carrierLogoEntries = useMemo(() => {
    const seen = new Set<string>()
    const rows: Array<CarrierLogoEntry> = []
    for (const deal of sortedDeals) {
      const img = deal.carrier.brandMark ?? deal.carrier.logo
      if ((!img?.url && !img?.id) || seen.has(deal.carrier.code)) continue
      seen.add(deal.carrier.code)
      rows.push({ code: deal.carrier.code, carrierName: deal.carrier.name, image: img })
      if (rows.length >= MAX_VISIBLE_CARRIER_LOGOS) break
    }
    return rows
  }, [sortedDeals])

  const handleCheckAvailability = useCallback(() => {
    dispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
    dispatch({ type: OfferPageActions.setFlights, flights: true })
  }, [dispatch])

  const handleOpenDealModal = useCallback(() => {
    if (!displayDeal) return
    showModal(
      <FlightDealModal
        activeDeal={displayDeal}
        showCheckAvailabilityCta={showCheckAvailabilityCta}
        onCheckAvailability={handleCheckAvailability}
      />,
    )
  }, [displayDeal, handleCheckAvailability, showCheckAvailabilityCta, showModal])

  const returnFlightsLabel = intl.formatMessage({
    id: '2Q_-G0NikQndLo7cUXU7',
    defaultMessage: 'Return flights',
    description: 'Form field label for return flight type in flight deal tile',
  })

  const oneWayFlightLabel = intl.formatMessage({
    id: 'aRHFsNUJ4p2hPoCiJJqF',
    defaultMessage: 'One way flight',
    description: 'Form field label for one-way flight type in flight deal tile',
  })

  const flightKindLabel = (cheapestDeal?.fareType ?? fallbackFlightPrice?.fareType) === 'oneWay' ? oneWayFlightLabel : returnFlightsLabel

  const bannerAnnouncement = useMemo(
    () => {
      if (displayDeal) {
        return intl.formatMessage(
          {
            id: '5hi8qwFSkdn69aJfIS0A',
            defaultMessage:
              '{flightKind} from an indicative per-person fare. Opens flight promotion details.',
            description:
              'Accessibility label for the bundled hotel flight merchandising banner that opens tactical flight deal details.',
          },
          { flightKind: flightKindLabel },
        )
      }

      return intl.formatMessage(
        {
          id: 'm6PJX-PVF4NTNmQlarQ0',
          defaultMessage:
            '{flightKind} from an indicative per-person fare.',
          description:
            'Accessibility label for the bundled hotel flight merchandising banner using calculated flight pricing.',
        },
        { flightKind: flightKindLabel },
      )
    },
    [displayDeal, flightKindLabel, intl],
  )

  if (!displayPrice) {
    return null
  }

  const logoContent = (
    <>
      {carrierLogoEntries.map((entry, index) => (
        <LogoRing key={entry.code} className={getLogoRingClassName(index)}>
          <CarrierLogoImage
            image={entry.image}
            height={20}
            alt={intl.formatMessage(
              {
                id: 'dEm8vCGBtEDJVX4iKDtL',
                defaultMessage: 'Logo of {carrierName}',
                description: 'Alt text for airline carrier logo on flight deal tile',
              },
              { carrierName: entry.carrierName },
            )}
          />
        </LogoRing>
      ))}
      <LogoRing className={getLogoRingClassName(carrierLogoEntries.length)}>
        <LinePlaneDepartureIcon size="S" colour="neutral-two" />
      </LogoRing>
    </>
  )

  const bannerContent = (
    <BannerSurface
      direction="horizontal"
      verticalAlign="center"
      horizontalAlign="start"
      gap={8}
      data-testid="bundled-flight-merchandising-banner"
      aria-label={bannerAnnouncement}
    >
      <Group direction="horizontal" verticalAlign="center" shrink={false}>
        {logoContent}
      </Group>
      <Group direction="vertical" gap={2}>
        <BodyText variant="medium" weight="normal">
          <FormattedMessage
            id="ZZR127LI-0XqUor1zbD7"
            defaultMessage="{flightKind} from {price}/{priceUnit}"
            description="Bundled-offer merchandising line showing flight type and indicative fare beside stacked airline logos on the flash hotel summary."
            values={{
              flightKind: flightKindLabel,
              priceUnit: priceUnitLabel,
              price: (
                <strong key="bundled-flight-price">
                  <FormatCurrency value={displayPrice} format="roundedDollar" />
                </strong>
              ),
            }}
          />
        </BodyText>
      </Group>
    </BannerSurface>
  )

  if (!displayDeal) {
    return <BannerContainer>{bannerContent}</BannerContainer>
  }

  return (
    <BannerButton onClick={handleOpenDealModal} type="button">
      {bannerContent}
    </BannerButton>
  )
}

export default FlashHotelBundledFlightMerchandisingBanner
